import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { 
  Search, 
  Clock, 
  BookOpen, 
  ChevronRight,
  Filter,
  Sparkles
} from 'lucide-react';

const SkillPaths = () => {
  const { api } = useAuth();
  const [paths, setPaths] = useState([]);
  const [filteredPaths, setFilteredPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  useEffect(() => {
    fetchPaths();
  }, []);

  useEffect(() => {
    filterPaths();
  }, [paths, searchQuery, categoryFilter, difficultyFilter]);

  const fetchPaths = async () => {
    try {
      const response = await api.get('/skill-paths');
      setPaths(response.data);
    } catch (error) {
      console.error('Error fetching skill paths:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPaths = () => {
    let filtered = [...paths];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        p => p.title.toLowerCase().includes(query) || 
             p.description.toLowerCase().includes(query) ||
             p.skills.some(s => s.toLowerCase().includes(query))
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }

    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(p => p.difficulty === difficultyFilter);
    }

    setFilteredPaths(filtered);
  };

  const categories = [...new Set(paths.map(p => p.category))];

  const difficultyColors = {
    beginner: 'bg-green-500/20 text-green-500',
    intermediate: 'bg-yellow-500/20 text-yellow-500',
    advanced: 'bg-red-500/20 text-red-500'
  };

  const difficultyLabels = {
    beginner: 'Principiante',
    intermediate: 'Intermedio',
    advanced: 'Avanzado'
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-72 rounded-2xl bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="skill-paths-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light">
            Rutas de <span className="text-gradient font-medium">Habilidad</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Elige tu camino de aprendizaje personalizado
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar rutas, habilidades..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="difficulty-filter">
            <SelectValue placeholder="Dificultad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las dificultades</SelectItem>
            <SelectItem value="beginner">Principiante</SelectItem>
            <SelectItem value="intermediate">Intermedio</SelectItem>
            <SelectItem value="advanced">Avanzado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Paths Grid */}
      {filteredPaths.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
          {filteredPaths.map((path, index) => (
            <Link 
              key={path.id} 
              to={`/skill-paths/${path.id}`}
              className="block"
              data-testid={`skill-path-card-${index}`}
            >
              <Card className="glass-card h-full overflow-hidden group cursor-pointer hover:border-primary/30 transition-all duration-300">
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${path.image_url || 'https://images.unsplash.com/photo-1642792347953-41639e0175b4'})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge className={difficultyColors[path.difficulty]}>
                      {difficultyLabels[path.difficulty]}
                    </Badge>
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <Badge variant="secondary">{path.category}</Badge>
                  </div>
                </div>

                <CardContent className="p-5 space-y-4">
                  <div>
                    <h3 className="text-lg font-medium mb-1 group-hover:text-primary transition-colors">
                      {path.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {path.description}
                    </p>
                  </div>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-1.5">
                    {path.skills.slice(0, 4).map(skill => (
                      <Badge key={skill} variant="outline" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                    {path.skills.length > 4 && (
                      <Badge variant="outline" className="text-xs">
                        +{path.skills.length - 4}
                      </Badge>
                    )}
                  </div>

                  {/* Meta */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {path.estimated_hours}h
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {path.courses_count} cursos
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No se encontraron rutas</h3>
          <p className="text-muted-foreground mb-4">
            Intenta ajustar los filtros de búsqueda
          </p>
          <Button variant="outline" onClick={() => {
            setSearchQuery('');
            setCategoryFilter('all');
            setDifficultyFilter('all');
          }}>
            Limpiar filtros
          </Button>
        </div>
      )}
    </div>
  );
};

export default SkillPaths;
